import { useState, useEffect, useRef } from 'react';
const STORAGE_KEY = 'lessonManagerStudents';
const KV_API = 'https://mustgym-api.mustraininggym.workers.dev';

function loadStudents() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

function saveStudents(students) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(students));
}

async function kvPutStudents(students) {
  try {
    const res = await fetch(`${KV_API}/students`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(students),
    });
    if (!res.ok) console.error('KV PUT /students failed', res.status);
  } catch(e) {
    console.error('KV sync failed', e);
  }
}

// readyToSync：由 App.js 傳入，代表 KV 初始讀取已完成，可以安全上傳
export function useStudents(readyToSync = false) {
  const [students, setStudents] = useState(loadStudents);
  const initializedRef = useRef(false);
  const readyToSyncRef = useRef(false);

  useEffect(() => {
    readyToSyncRef.current = readyToSync;
  }, [readyToSync]);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      saveStudents(students);
      return;
    }
    saveStudents(students);
    if (readyToSyncRef.current) {
      kvPutStudents(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
    }
  }, [students]);

  function addStudent(student) {
    const newStudent = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      name: '',
      gender: '',
      birthday: '',
      phone: '',
      mainColorId: '',
      graduated: false,
      hasTrial: false,
      trialDate: '',
      trialTime: '',
      trialColorId: '',
      trialNotes: '',
      payments: [],
      notes: '',
      baseRate: 0, // ★ 新增：單堂定價（Credit-Based System）
      ...student,
    };
    setStudents(prev => [...prev, newStudent]);
    return newStudent;
  }

  function updateStudent(id, updates) {
    setStudents(prev =>
      prev.map(s => s.id === id ? { ...s, ...updates } : s)
    );
  }

  function deleteStudent(id) {
    setStudents(prev => prev.filter(s => s.id !== id));
  }

  function addPayment(studentId, payment) {
    setStudents(prev =>
      prev.map(s => {
        if (s.id !== studentId) return s;
        const existingPayments = Array.isArray(s.payments) ? s.payments : [];
        return {
          ...s,
          payments: [...existingPayments, {
            id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            date: '',
            lessons: 0,
            amount: 0,
            notes: '',
            ...payment,
          }],
        };
      })
    );
  }

  // 刪除付款紀錄
  // 若該筆有 linkedId（代付/被代付），同時刪除所有學員中相同 linkedId 的紀錄
  function deletePayment(studentId, paymentId) {
    setStudents(prev => {
      // 先找到要刪除的那筆，取得 linkedId
      const targetStudent = prev.find(s => s.id === studentId);
      const targetPayment = (targetStudent?.payments || []).find(p => p.id === paymentId);
      const linkedId = targetPayment?.linkedId;

      return prev.map(s => {
        if (linkedId) {
          // 有 linkedId：刪除所有學員中 linkedId 相同的紀錄（包含自己那筆）
          return {
            ...s,
            payments: (s.payments || []).filter(p => p.linkedId !== linkedId),
          };
        }
        // 一般付款：只刪除指定學員的指定紀錄
        if (s.id !== studentId) return s;
        return {
          ...s,
          payments: (s.payments || []).filter(p => p.id !== paymentId),
        };
      });
    });
  }

  // ★ 需求1：更新單筆付款紀錄
  function updatePayment(studentId, paymentId, updates) {
    setStudents(prev =>
      prev.map(s => {
        if (s.id !== studentId) return s;
        return {
          ...s,
          payments: (s.payments || []).map(p =>
            p.id === paymentId ? { ...p, ...updates } : p
          ),
        };
      })
    );
  }

  // ★ 舊有輔助函式（保留相容性）
  function getPurchased(student) {
    return (student.payments || []).reduce((sum, p) => sum + (Number(p.lessons) || 0), 0);
  }

  function getTotalAmount(student) {
    return (student.payments || []).reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  }

  function getPerLesson(student) {
    const purchased = getPurchased(student);
    const total = getTotalAmount(student);
    return purchased > 0 ? Math.round(total / purchased) : 0;
  }

  // ★ Credit-Based System 餘額計算
  // 剩餘餘額 = 總繳費 + 被代付金額 - 自己上課次數 × baseRate - 自己代付他人的金額
  function getBalance(student, usedLessons) {
    const totalPaid = getTotalAmount(student);
    const baseRate = Number(student.baseRate) || 0;
    // 自己上課消耗
    const selfCost = usedLessons * baseRate;
    // 代付他人消耗（proxyType === 'payer'）
    const proxyCost = (student.payments || []).reduce((sum, p) => {
      if (p.isProxy && p.proxyType === 'payer') return sum + (Number(p.proxyAmount) || 0);
      return sum;
    }, 0);
    // ★ 需求1：被代付收益（proxyType === 'beneficiary'，proxyAmount 存實際金額）
    const proxyReceived = (student.payments || []).reduce((sum, p) => {
      if (p.isProxy && p.proxyType === 'beneficiary') return sum + (Number(p.proxyAmount) || 0);
      return sum;
    }, 0);
    return totalPaid + proxyReceived - selfCost - proxyCost;
  }

  // 代付上課紀錄
  // payerStudentId: 代付人 ID
  // beneficiaryStudentId: 被代付人 ID
  // beneficiaryBaseRate: 被代付人的 baseRate
  // lessonInfo: { date, notes }
  // 回傳 { ok }（不再檢查餘額，允許負數，由 UI 顯示催費）
  function addProxyLesson(payerStudentId, beneficiaryStudentId, beneficiaryBaseRate, lessonInfo) {
    const proxyId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;

    setStudents(prev => prev.map(s => {
      // 代付人：扣除 proxyAmount
      if (s.id === payerStudentId) {
        const beneficiary = prev.find(x => x.id === beneficiaryStudentId);
        const bName = beneficiary?.name || '他人';
        return {
          ...s,
          payments: [...(s.payments || []), {
            id: proxyId + '_payer',
            date: lessonInfo.date || '',
            lessons: 0,
            amount: 0,
            proxyAmount: beneficiaryBaseRate,
            notes: `代付 ${bName}（$${beneficiaryBaseRate.toLocaleString()}）${lessonInfo.notes ? ' — ' + lessonInfo.notes : ''}`,
            isProxy: true,
            proxyType: 'payer',
            linkedId: proxyId,
          }],
        };
      }
      // 被代付人：存入 proxyAmount，讓 getBalance 加回來
      if (s.id === beneficiaryStudentId) {
        const payer = prev.find(x => x.id === payerStudentId);
        const pName = payer?.name || '他人';
        return {
          ...s,
          payments: [...(s.payments || []), {
            id: proxyId + '_beneficiary',
            date: lessonInfo.date || '',
            lessons: 0,
            amount: 0,
            proxyAmount: beneficiaryBaseRate,
            notes: `由 ${pName} 代付（$${beneficiaryBaseRate.toLocaleString()}）${lessonInfo.notes ? ' — ' + lessonInfo.notes : ''}`,
            isProxy: true,
            proxyType: 'beneficiary',
            linkedId: proxyId,
          }],
        };
      }
      return s;
    }));

    return { ok: true };
  }

  return {
    students,
    addStudent,
    updateStudent,
    deleteStudent,
    addPayment,
    deletePayment,
    updatePayment,
    getPurchased,
    getTotalAmount,
    getPerLesson,
    getBalance,
    addProxyLesson,
  };
}
